import './style.css'
import { loadUser, saveUser, avatarUrl, randomName, randomSeed, listPresets, isPresetSeed } from './avatars'
import { Ripple } from './ripple'
import {
  renderDots,
  renderPulseDot,
  showPointCard,
  closePointCard,
  setCardContext,
} from './points'
import { uploadImage, listGallery, listPoints, generatePoint, regeneratePoint, deleteImage, HttpError } from './api'
import { compressImage } from './image-utils'
import type { ImageMeta, Point, User } from './types'

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T

let user: User = loadUser()
let currentImage: ImageMeta | null = null
let currentPoints: Point[] = []
let ripple: Ripple | null = null

// ---------- header / me ----------

function renderMe() {
  ;($('me-avatar') as HTMLImageElement).src = avatarUrl(user.avatarSeed, 64)
  $('me-name').textContent = user.name
}

$('me-btn').addEventListener('click', () => openMeEditor())

function openMeEditor() {
  const root = $('me-portal')
  root.innerHTML = ''
  const overlay = document.createElement('div')
  overlay.className = 'fixed inset-0 z-50 bg-ink-900/30 backdrop-blur-[2px]'
  overlay.addEventListener('click', () => (root.innerHTML = ''))

  const card = document.createElement('div')
  card.className = 'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[400px] max-w-[92vw] rounded-2xl bg-white p-5 shadow-pop'
  card.addEventListener('click', (e) => e.stopPropagation())
  const presets = listPresets()
  card.innerHTML = `
    <div class="mb-4">
      <div class="text-[12px] text-ink-500 mb-2">头像</div>
      <div id="me-edit-grid" class="grid grid-cols-7 gap-2"></div>
      <input type="file" id="me-edit-upload" accept="image/jpeg,image/png,image/webp" class="hidden" />
    </div>
    <label class="block text-[12px] text-ink-500 mb-1">名字</label>
    <input id="me-edit-name" class="w-full px-3 py-2 rounded-lg border border-ink-100 focus:border-accent focus:outline-none text-[14px]" />
    <div class="flex items-center justify-end gap-2 mt-4">
      <button id="me-edit-cancel" class="text-[13px] px-3 py-2 text-ink-500">取消</button>
      <button id="me-edit-save" class="text-[13px] px-4 py-2 rounded-full bg-ink-900 text-paper hover:bg-ink-700 transition-colors">保存</button>
    </div>
  `
  root.appendChild(overlay)
  root.appendChild(card)

  let seed = user.avatarSeed
  const grid = card.querySelector('#me-edit-grid') as HTMLDivElement
  const nameInput = card.querySelector('#me-edit-name') as HTMLInputElement
  const uploadInputEl = card.querySelector('#me-edit-upload') as HTMLInputElement
  nameInput.value = user.name

  function renderGrid() {
    const presetTiles = presets
      .map(
        (s) => `
      <button data-seed="${s}" class="aspect-square rounded-full overflow-hidden ring-2 transition-all ${
        s === seed ? 'ring-accent scale-105' : 'ring-transparent hover:ring-ink-300 opacity-80 hover:opacity-100'
      }">
        <img src="${avatarUrl(s)}" class="w-full h-full object-cover" alt="${s}" />
      </button>`,
      )
      .join('')
    const customPreviewTile =
      seed && !isPresetSeed(seed)
        ? `<button data-seed="${seed}" class="aspect-square rounded-full overflow-hidden ring-2 ring-accent scale-105 relative">
             <img src="${avatarUrl(seed)}" class="w-full h-full object-cover" />
           </button>`
        : ''
    const uploadTile = `
      <button data-action="upload" class="aspect-square rounded-full border-2 border-dashed border-ink-300 hover:border-accent hover:text-accent text-ink-500 transition-colors flex items-center justify-center">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
      </button>`
    grid.innerHTML = presetTiles + customPreviewTile + uploadTile

    grid.querySelectorAll('button[data-seed]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const newSeed = (btn as HTMLElement).dataset.seed!
        seed = newSeed
        if (isPresetSeed(newSeed)) {
          // sync name input with the preset's name
          nameInput.value = newSeed
        }
        renderGrid()
      })
    })
    grid.querySelector('button[data-action="upload"]')!.addEventListener('click', () => {
      uploadInputEl.click()
    })
  }
  renderGrid()

  uploadInputEl.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (!file) return
    try {
      const compressed = await compressImage(file, 512)
      const fd = new FormData()
      fd.append('file', compressed)
      const r = await fetch('/api/avatar/upload', { method: 'POST', body: fd })
      if (!r.ok) throw new Error(`upload failed: ${r.status}`)
      const out = (await r.json()) as { url: string }
      seed = out.url
      renderGrid()
    } catch (err) {
      console.error(err)
      alert('头像上传失败')
    }
    ;(e.target as HTMLInputElement).value = ''
  })
  card.querySelector('#me-edit-cancel')!.addEventListener('click', () => (root.innerHTML = ''))
  card.querySelector('#me-edit-save')!.addEventListener('click', () => {
    user = { ...user, name: nameInput.value.trim() || randomName(), avatarSeed: seed }
    saveUser(user)
    renderMe()
    root.innerHTML = ''
  })
}

// ---------- picker ----------

const picker = $('picker')
const stage = $('stage')

function showPicker() {
  stage.classList.add('hidden')
  picker.classList.remove('hidden')
  refreshGallery()
}

function showStage(image: ImageMeta) {
  picker.classList.add('hidden')
  stage.classList.remove('hidden')
  currentImage = image
  $('stage-meta').textContent = explorationLabel(image.userCount ?? 0, image.pointCount ?? 0)
  loadStage(image)
}

function explorationLabel(users: number, points: number): string {
  if (points === 0) return '还没人点过这里'
  if (users === 1) return `1 人探索了 ${points} 个点`
  return `${users} 人探索了 ${points} 个点`
}

$('back-btn').addEventListener('click', showPicker)
$('new-upload-btn').addEventListener('click', () => triggerUpload())

const uploadInput = $('upload-input') as HTMLInputElement
uploadInput.addEventListener('change', async (e) => {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (!file) return
  await handleUpload(file)
  ;(e.target as HTMLInputElement).value = ''
})

function triggerUpload() {
  uploadInput.click()
}

// Drag & drop on the picker dropzone
const dropzone = document.getElementById('dropzone')
if (dropzone) {
  const setActive = (active: boolean) => {
    dropzone.classList.toggle('border-accent', active)
    dropzone.classList.toggle('bg-accent/[0.06]', active)
    dropzone.classList.toggle('border-ink-300', !active)
  }
  ;['dragenter', 'dragover'].forEach((evt) =>
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault()
      setActive(true)
    }),
  )
  ;['dragleave', 'dragend'].forEach((evt) =>
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault()
      setActive(false)
    }),
  )
  dropzone.addEventListener('drop', async (e) => {
    e.preventDefault()
    setActive(false)
    const file = (e as DragEvent).dataTransfer?.files?.[0]
    if (file && /^image\/(jpeg|png|webp)$/.test(file.type)) {
      await handleUpload(file)
    }
  })
  // Also catch a global drag-over so the browser doesn't open the file
  window.addEventListener('dragover', (e) => e.preventDefault())
  window.addEventListener('drop', (e) => e.preventDefault())
}

async function handleUpload(file: File) {
  setStatus('准备图片…')
  try {
    const compressed = await compressImage(file, 3000)
    setStatus('上传中…')
    const image = await uploadImage(compressed, user)
    setStatus('')
    showStage(image)
  } catch (e: any) {
    setStatus(`上传失败：${e.message || e}`)
  }
}

async function refreshGallery() {
  const root = $('gallery')
  root.innerHTML = `<div class="text-ink-500 text-[12px] text-center">加载中…</div>`
  try {
    const images = await listGallery()
    if (images.length === 0) {
      root.innerHTML = `<div class="text-ink-500 text-[12px] text-center">还没人传过图，做第一个吧。</div>`
      return
    }
    root.innerHTML = `
      <div class="text-[13px] text-ink-500 mb-3">大家都点过这些图</div>
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        ${images
          .map((img) => {
            const u = img.userCount ?? 0
            const p = img.pointCount ?? 0
            const meta = p === 0 ? '还没人点' : `${u} 人 · ${p} 个点`
            const uploader = img.uploaderName
              ? `<span class="flex items-center gap-1.5 min-w-0 max-w-[60%]">
                   <img src="${avatarUrl(img.uploaderAvatarSeed || img.uploaderName)}" class="w-5 h-5 rounded-full flex-shrink-0 ring-1 ring-white/60" />
                   <span class="text-[11px] text-ink-900 font-medium truncate">${img.uploaderName}</span>
                 </span>`
              : `<span></span>`
            const canDelete = img.uploaderId && img.uploaderId === user.id
            const delBtn = canDelete
              ? `<button data-del-id="${img.id}" class="absolute top-2 right-2 w-7 h-7 rounded-full bg-ink-900/60 hover:bg-ink-900/85 text-white flex items-center justify-center backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity" aria-label="删除这张图" title="删除这张图">
                   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14"/></svg>
                 </button>`
              : ''
            return `
          <div class="group relative aspect-[4/3] rounded-xl overflow-hidden shadow-card" data-card-img="${img.id}">
            <button class="block w-full h-full text-left" data-img-id="${img.id}">
              <img src="${img.url}" loading="lazy" class="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
              <div class="absolute inset-x-2 bottom-2 px-2.5 py-1.5 flex items-center justify-between gap-2 rounded-full bg-white/45 backdrop-blur-2xl border border-white/15 shadow-[0_1px_4px_rgba(0,0,0,0.08)] supports-[backdrop-filter]:bg-white/30">
                ${uploader}
                <span class="text-[11px] text-ink-700 flex-shrink-0">${meta}</span>
              </div>
            </button>
            ${delBtn}
          </div>`
          })
          .join('')}
      </div>
    `
    root.querySelectorAll('[data-img-id]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset.imgId!
        const meta = images.find((i) => i.id === id)
        if (meta) showStage(meta)
      })
    })
    root.querySelectorAll('[data-del-id]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation()
        const id = (btn as HTMLElement).dataset.delId!
        if (!confirm('删了就找不回来了，确定？')) return
        try {
          await deleteImage(id, user)
          // 直接把卡片淡出移除
          const card = (btn as HTMLElement).closest('[data-card-img]') as HTMLElement | null
          if (card) {
            card.style.transition = 'opacity .25s, transform .25s'
            card.style.opacity = '0'
            card.style.transform = 'scale(.96)'
            setTimeout(() => card.remove(), 260)
          }
        } catch (err: any) {
          alert(`删不掉：${err.message || err}`)
        }
      })
    })
  } catch (e: any) {
    root.innerHTML = `<div class="text-ink-500 text-[12px] text-center">加载失败</div>`
  }
}

// ---------- stage ----------

const sourceImg = $('source-img') as HTMLImageElement
const rippleCanvas = $('ripple-canvas') as HTMLCanvasElement
const dotsLayer = $('dots-layer')
const hitLayer = $('hit-layer')
const container = $('ripple-container')
const dramaOverlay = $('drama-overlay') as HTMLImageElement

// 显示 drama 大图：原图淡出，drama 渐入。点击大图回到原图。
function showDramaOverlay(p: Point) {
  if (!p.dramaUrl) return
  closePointCard()
  // 预加载新图，等加载好再做 fade-in，避免空白闪
  const tmp = new Image()
  const reveal = () => {
    dramaOverlay.src = p.dramaUrl!
    dramaOverlay.style.pointerEvents = 'auto'
    // 隐藏 dots，专注看 drama
    dotsLayer.style.opacity = '0'
    dotsLayer.style.pointerEvents = 'none'
    requestAnimationFrame(() => requestAnimationFrame(() => {
      dramaOverlay.classList.remove('result-image-hidden')
    }))
  }
  tmp.onload = reveal
  tmp.onerror = reveal
  tmp.src = p.dramaUrl
}

function hideDramaOverlay() {
  dramaOverlay.classList.add('result-image-hidden')
  dramaOverlay.style.pointerEvents = 'none'
  dotsLayer.style.opacity = ''
  dotsLayer.style.pointerEvents = ''
}

// 点击 drama 覆盖层 → 淡回原图
dramaOverlay.addEventListener('click', (e) => {
  e.stopPropagation()
  hideDramaOverlay()
})

function loadStage(image: ImageMeta) {
  sourceImg.crossOrigin = 'anonymous'
  sourceImg.src = image.url
  sourceImg.onload = () => {
    if (ripple) {
      // recreate so the new image is uploaded as texture
    }
    ripple = new Ripple(rippleCanvas, sourceImg)
  }
  loadPoints()
}

async function loadPoints() {
  if (!currentImage) return
  try {
    currentPoints = await listPoints(currentImage.id)
  } catch {
    currentPoints = []
  }
  setCardContext(
    currentPoints,
    container,
    user,
    (np) => {
      const idx = currentPoints.findIndex((x) => x.id === np.id)
      if (idx >= 0) currentPoints[idx] = np
    },
    (p) => runRegenAtPoint(p),
  )
  // dots 只渲染直接 image 上的点（parentPointId 为空）— 链条点的坐标对原图没意义
  const directPoints = currentPoints.filter((p) => !p.parentPointId)
  renderDots(
    dotsLayer,
    directPoints,
    (p) => showPointCard(p, container),  // hover (avatar/anchor) → 出卡片
    (p) => showDramaOverlay(p),          // click 白锚点 → 渐入 drama 大图
  )
  renderPointsList()
}

function timeAgoZh(ts: number): string {
  const diff = Math.max(0, Date.now() - ts)
  const s = Math.floor(diff / 1000)
  if (s < 30) return '刚刚'
  if (s < 60) return `${s} 秒前`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m} 分钟前`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} 小时前`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d} 天前`
  return new Date(ts).toLocaleDateString('zh-CN')
}

function renderPointsList() {
  const root = $('points-list')
  const done = [...currentPoints]
    .filter((p) => p.status === 'done')
    .sort((a, b) => a.createdAt - b.createdAt) // 时间轴：旧 → 新

  if (done.length === 0) {
    root.innerHTML = ''
    return
  }
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

  root.innerHTML = `
    <div class="text-[13px] text-ink-500 mb-6 text-center">大家点过的剧情 · ${done.length}</div>
    <ol class="relative max-w-5xl mx-auto">
      <!-- 中轴：mobile 在左 10px、desktop 居中 -->
      <span class="absolute top-2 bottom-2 w-px bg-ink-300/80 left-[10px] sm:left-1/2"></span>
      ${done
        .map((p, i) => {
          const directionTag = p.direction === 'before' ? '前情' : '后续'
          const chainTag = p.parentPointId
            ? `<span class="text-[10px] text-ink-500 bg-ink-100 rounded-full px-1.5 py-0.5">套娃</span>`
            : ''
          const onLeft = i % 2 === 0
          // mobile：所有内容都在右侧（pl-8）
          // desktop：奇偶交替放 col 1 / col 2
          const colClass = onLeft ? 'sm:col-start-1 sm:text-right' : 'sm:col-start-2 sm:text-left'
          const metaJustify = onLeft ? 'sm:justify-end' : 'sm:justify-start'
          const cardAlign = onLeft ? 'sm:ml-auto' : 'sm:mr-auto'
          return `
        <li class="relative pb-10 last:pb-0 pl-8 sm:pl-0 sm:grid sm:grid-cols-2 sm:gap-10">
          <!-- 时间节点 -->
          <span class="absolute top-2 w-3 h-3 rounded-full bg-accent left-[10px] -translate-x-1/2 sm:left-1/2 sm:-translate-x-1/2" style="box-shadow: 0 0 0 4px #fafaf7;"></span>

          <div class="${colClass}">
            <!-- 时间 + 用户元信息 -->
            <div class="flex items-center flex-wrap gap-2 text-[11px] text-ink-500 mb-2 ${metaJustify}">
              <img src="${avatarUrl(p.userAvatarSeed)}" class="w-4 h-4 rounded-full ring-1 ring-ink-100" />
              <span class="text-ink-900 font-medium">${escape(p.userName)}</span>
              <span class="text-ink-300">·</span>
              <span>${escape(timeAgoZh(p.createdAt))}</span>
              ${chainTag}
              <span class="text-accent-deep bg-accent/10 rounded-full px-1.5 py-0.5 text-[10px]">${directionTag} · ${escape(p.timeOffset)}</span>
            </div>

            <!-- 卡片 -->
            <button data-list-pid="${p.id}" class="group block rounded-xl overflow-hidden shadow-card bg-white w-full max-w-[440px] text-left transition-shadow hover:shadow-pop ${cardAlign}">
              ${
                p.dramaUrl
                  ? `<div class="aspect-[4/3] overflow-hidden bg-paper-deep">
                       <img src="${p.dramaUrl}" loading="lazy" class="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" />
                     </div>`
                  : ''
              }
              <p class="px-4 py-3 text-[13px] leading-[1.55] text-ink-900 text-left">${escape(p.sceneZh)}</p>
            </button>
          </div>
        </li>`
        })
        .join('')}
    </ol>
  `
  root.querySelectorAll<HTMLElement>('[data-list-pid]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const pid = btn.dataset.listPid!
      const p = currentPoints.find((x) => x.id === pid)
      if (p) showPointCard(p, container)
    })
  })
}

hitLayer.addEventListener('click', async (ev) => {
  if (!currentImage) return
  const rect = container.getBoundingClientRect()
  const xRatio = (ev.clientX - rect.left) / rect.width
  const yRatio = (ev.clientY - rect.top) / rect.height
  if (xRatio < 0 || xRatio > 1 || yRatio < 0 || yRatio > 1) return
  await runGenerate(xRatio, yRatio)
})

let inflight = 0

// 客户端节流：10 秒内最多 4 次（与后端一致）。直接拦在前面，避免发请求
const RATE_WINDOW_MS = 10000
const RATE_LIMIT = 4
const recentGenClicks: number[] = []

function tryAcquireRateSlot(): boolean {
  const now = Date.now()
  while (recentGenClicks.length > 0 && recentGenClicks[0] < now - RATE_WINDOW_MS) {
    recentGenClicks.shift()
  }
  if (recentGenClicks.length >= RATE_LIMIT) return false
  recentGenClicks.push(now)
  return true
}

function showRateHint() {
  setStatus('喘口气，10 秒内最多 4 次')
  setTimeout(() => {
    if (inflight === 0) setStatus('')
  }, 1800)
}

// 生成时轮播的小文案 —— 只给氛围，不暴露机制
const STATUS_CYCLE = [
  '让画面起雾…',
  '等显影…',
  '把暗房灯关上…',
  '画面在低语…',
  '让边缘软下来…',
  '拢起一道光…',
  '等水银凝结…',
  '让影子说话…',
  '让一些东西浮出来…',
  '翻一下这张图的反面…',
  '在底片背面写字…',
  '让光线绕个弯…',
  '再等 5–10 秒，时空裂开了一条缝…',
  '快到了，奇迹时刻还差几秒…',
  '时空隧道再走 5 秒…',
  '奇迹正在路上…',
]

let statusTimer: number | null = null

function startStatusCycle() {
  // 随机起点 + 顺序遍历，每次点都觉得是新文案
  let i = Math.floor(Math.random() * STATUS_CYCLE.length)
  setStatus(STATUS_CYCLE[i])
  if (statusTimer) clearInterval(statusTimer)
  statusTimer = window.setInterval(() => {
    i = (i + 1) % STATUS_CYCLE.length
    setStatus(STATUS_CYCLE[i])
  }, 2200)
}

function stopStatusCycle() {
  if (statusTimer) {
    clearInterval(statusTimer)
    statusTimer = null
  }
}

async function runGenerate(xRatio: number, yRatio: number) {
  if (!currentImage) return
  if (!tryAcquireRateSlot()) {
    showRateHint()
    return
  }
  closePointCard()
  inflight++
  const stopRipple = ripple?.start(xRatio, yRatio, true)
  const removePulseDot = renderPulseDot(dotsLayer, xRatio, yRatio)
  startStatusCycle()

  try {
    const point = await generatePoint({
      imageId: currentImage.id,
      user,
      xRatio,
      yRatio,
    })
    currentPoints.push(point)
    setCardContext(
      currentPoints,
      container,
      user,
      (np) => {
        const idx = currentPoints.findIndex((x) => x.id === np.id)
        if (idx >= 0) currentPoints[idx] = np
        else currentPoints.push(np)
      },
      (p) => runRegenAtPoint(p),
    )
    const directPoints = currentPoints.filter((x) => !x.parentPointId)
    renderDots(
      dotsLayer,
      directPoints,
      (p) => showPointCard(p, container),
      (p) => showDramaOverlay(p),
    )
    renderPointsList()
    stopStatusCycle()
    setStatus('')
    showPointCard(point, container)
  } catch (e: any) {
    stopStatusCycle()
    setStatus(`没出来：${e.message || e}`)
  } finally {
    inflight--
    stopRipple?.()
    removePulseDot()
    if (inflight === 0) {
      setTimeout(() => {
        if (inflight === 0) setStatus('')
      }, 2000)
    }
  }
}

// 重新生成一个已有点位：跟初始 generate 同款 UX —— 涟漪 + 状态轮播
async function runRegenAtPoint(p: Point) {
  if (!tryAcquireRateSlot()) {
    closePointCard()
    showRateHint()
    return
  }
  closePointCard()
  inflight++
  const stopRipple = ripple?.start(p.xRatio, p.yRatio, true)
  const removePulseDot = renderPulseDot(dotsLayer, p.xRatio, p.yRatio)
  startStatusCycle()
  try {
    const newP = await regeneratePoint(p.id, user)
    const idx = currentPoints.findIndex((x) => x.id === newP.id)
    if (idx >= 0) currentPoints[idx] = newP
    setCardContext(
      currentPoints,
      container,
      user,
      (np) => {
        const i2 = currentPoints.findIndex((x) => x.id === np.id)
        if (i2 >= 0) currentPoints[i2] = np
        else currentPoints.push(np)
      },
      (pp) => runRegenAtPoint(pp),
    )
    const direct2 = currentPoints.filter((x) => !x.parentPointId)
    renderDots(
      dotsLayer,
      direct2,
      (pp) => showPointCard(pp, container),
      (pp) => showDramaOverlay(pp),
    )
    renderPointsList()
    stopStatusCycle()
    setStatus('')
    showPointCard(newP, container)
  } catch (e: any) {
    stopStatusCycle()
    if (e instanceof HttpError && e.status === 429) {
      setStatus(e.message || '点得太快了，喘口气再来')
    } else {
      setStatus(`没出来：${e.message || e}`)
    }
  } finally {
    inflight--
    stopRipple?.()
    removePulseDot()
    if (inflight === 0) {
      setTimeout(() => {
        if (inflight === 0) setStatus('')
      }, 2000)
    }
  }
}

function setStatus(msg: string) {
  const el = $('status-bar')
  if (msg) {
    // 短淡入：opacity 0 → 1 让轮播文字像翻页
    el.style.opacity = '0'
    el.innerHTML = `<span class="spark"></span>${msg}`
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.opacity = '1'
      })
    })
  } else {
    el.style.opacity = '0'
    setTimeout(() => {
      if (el.style.opacity === '0') el.innerHTML = ''
    }, 250)
  }
}

// ---------- boot ----------

renderMe()
showPicker()
